import React, { createContext, useCallback, useContext, useMemo } from 'react';

import type { Kitchen } from '@rta/rta-blueprint-library';

export type BlueprintOverridesContextValue = {
	overrides: Record<string, string>;
	setOverride: (key: string, value: string) => void;
};

const BlueprintOverridesContext = createContext<BlueprintOverridesContextValue | null>(null);

export type BlueprintOverridesProviderProps = {
	kitchen: Kitchen | null;
	children: React.ReactNode;
};

export const BlueprintOverridesProvider = (props: BlueprintOverridesProviderProps): React.ReactElement => {
	const { kitchen, children } = props;

	const setOverride = useCallback((key: string, value: string): void => {
		kitchen?.setBlueprintOverride(key, value);
	}, [kitchen]);

	const value = useMemo((): BlueprintOverridesContextValue => ({
		overrides: kitchen?.blueprintOverrides ?? {},
		setOverride
	}), [kitchen?.blueprintOverrides, setOverride]);

	return (
		<BlueprintOverridesContext.Provider value={value}>
			{children}
		</BlueprintOverridesContext.Provider>
	);
};

export const useBlueprintOverrides = (): BlueprintOverridesContextValue => {
	const ctx = useContext(BlueprintOverridesContext);
	if (!ctx) {
		return { overrides: {}, setOverride: () => {} };
	}
	return ctx;
};
